import React from 'react';

import { Nav, StickyCTA, Hero, Opportunity, Problem, Compare, ForWho } from './components/SectionsTop.jsx';
import { Curriculum, Coach, Testimonials, GuideCTA } from './components/SectionsMid.jsx';
import { Packages, FAQ, CTAFinal, Footer } from './components/SectionsBot.jsx';
import { CookieBanner } from './components/CookieBanner.jsx';
import { useTweaks, TweaksPanel, TweakSection, TweakRadio } from './components/TweaksPanel.jsx';
import { captureUtmFromUrl } from './lib/tracking.js';

// One-time side effects (run on module load — before React mounts).
// NOTE: initCalendlyListener() lives in main.jsx so it runs on every route,
// including /guide where this App component is not rendered.
captureUtmFromUrl();

const TWEAK_DEFAULTS = /*EDITMODE-BEGIN*/{
  "headlineVariant": "A",
  "accentHue": "brand",
  "showFeaturedBadge": true,
  "showVSL": true
}/*EDITMODE-END*/;

export default function App() {
  const [tweaks, setTweak] = useTweaks(TWEAK_DEFAULTS);

  // Scroll to URL anchor on initial load. Native browser scroll-to-fragment
  // fires BEFORE React renders the sections, so #pakete etc. don't work when
  // users land via Google Ads sitelinks or any direct deep-link with hash.
  // We retry via rAF (sections render in the first paint) and use 'auto' so
  // it's an instant jump, not a janky smooth-scroll on load.
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const hash = window.location.hash;
    if (!hash || hash.length < 2) return;
    const id = hash.slice(1);
    let attempts = 0;
    const tryScroll = () => {
      const target = document.getElementById(id);
      if (target) {
        target.scrollIntoView({ behavior: 'auto', block: 'start' });
        // Re-scroll after fonts/images settle so we land precisely (layout
        // shifts from font swap can change the target's y-position).
        setTimeout(() => {
          const t2 = document.getElementById(id);
          if (t2) t2.scrollIntoView({ behavior: 'auto', block: 'start' });
        }, 600);
        return;
      }
      if (attempts++ < 30) requestAnimationFrame(tryScroll);
    };
    requestAnimationFrame(tryScroll);
  }, []);

  React.useEffect(() => {
    const sections = document.querySelectorAll('section');
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('in'); });
    }, { threshold: 0, rootMargin: '0px 0px -8% 0px' });
    sections.forEach(s => {
      s.classList.add('reveal-on-scroll');
      const rect = s.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) {
        s.classList.add('in');
      }
      io.observe(s);
    });
    const safety = setTimeout(() => {
      document.querySelectorAll('section.reveal-on-scroll:not(.in)').forEach(s => {
        const rect = s.getBoundingClientRect();
        if (rect.top < window.innerHeight * 1.5) s.classList.add('in');
      });
    }, 1000);
    return () => { io.disconnect(); clearTimeout(safety); };
  }, []);

  React.useEffect(() => {
    const hueMap = {
      brand:    { accent: '#4dcb8b', accent2: '#6fe0a3', glow: 'rgba(77,203,139,0.45)',  soft: 'rgba(77,203,139,0.12)'  },
      stripe:   { accent: '#635bff', accent2: '#8b85ff', glow: 'rgba(99,91,255,0.45)',   soft: 'rgba(99,91,255,0.12)'   },
      sky:      { accent: '#0a84ff', accent2: '#4da3ff', glow: 'rgba(10,132,255,0.45)',  soft: 'rgba(10,132,255,0.12)'  },
      violet:   { accent: '#7c3aed', accent2: '#a78bfa', glow: 'rgba(124,58,237,0.45)',  soft: 'rgba(124,58,237,0.12)'  },
      gold:     { accent: '#d4a85a', accent2: '#e8c373', glow: 'rgba(212,168,90,0.45)',  soft: 'rgba(212,168,90,0.12)'  },
      lime:     { accent: '#a3e635', accent2: '#c5f266', glow: 'rgba(163,230,53,0.45)',  soft: 'rgba(163,230,53,0.12)'  },
      coral:    { accent: '#fb7185', accent2: '#fda4af', glow: 'rgba(251,113,133,0.45)', soft: 'rgba(251,113,133,0.12)' },
      crimson:  { accent: '#ef4444', accent2: '#f87171', glow: 'rgba(239,68,68,0.45)',   soft: 'rgba(239,68,68,0.12)'   },
      cyan:     { accent: '#06b6d4', accent2: '#22d3ee', glow: 'rgba(6,182,212,0.45)',   soft: 'rgba(6,182,212,0.12)'   },
      mono:     { accent: '#fafafa', accent2: '#ffffff', glow: 'rgba(255,255,255,0.30)', soft: 'rgba(255,255,255,0.08)' },
    };
    const h = hueMap[tweaks.accentHue] || hueMap.brand;
    document.documentElement.style.setProperty('--accent', h.accent);
    document.documentElement.style.setProperty('--accent-2', h.accent2);
    document.documentElement.style.setProperty('--accent-glow', h.glow);
    document.documentElement.style.setProperty('--accent-soft', h.soft);
  }, [tweaks.accentHue]);

  return (
    <>
      <Nav />
      <Hero headlineVariant={tweaks.headlineVariant} />
      <Opportunity />
      <Problem />
      <Compare />
      <Curriculum />
      <ForWho />
      <GuideCTA />
      <Coach />
      <Testimonials />
      <Packages />
      <FAQ />
      <CTAFinal />
      <Footer />
      <StickyCTA />
      <CookieBanner />

      {import.meta.env.DEV && (
        <TweaksPanel title="Tweaks">
          <TweakSection title="Hero">
            <TweakRadio
              label="Headline"
              value={tweaks.headlineVariant}
              onChange={(v) => setTweak('headlineVariant', v)}
              options={[
                { value: 'A', label: 'Klassik' },
                { value: 'B', label: 'Story' },
                { value: 'C', label: 'Konfront.' },
              ]}
            />
          </TweakSection>
          <TweakSection title="Akzentfarbe">
            <TweakRadio
              label="Hue"
              value={tweaks.accentHue}
              onChange={(v) => setTweak('accentHue', v)}
              options={[
                { value: 'brand',   label: 'Brand Green (PDF)' },
                { value: 'stripe',  label: 'Stripe Indigo' },
                { value: 'sky',     label: 'Apple Sky Blue' },
                { value: 'violet',  label: 'SaaS Violet' },
                { value: 'gold',    label: 'Money Gold' },
                { value: 'lime',    label: 'Electric Lime' },
                { value: 'coral',   label: 'Coral Sunset' },
                { value: 'crimson', label: 'Crimson Power' },
                { value: 'cyan',    label: 'Cyan Future' },
                { value: 'mono',    label: 'Pure Mono' },
              ]}
            />
          </TweakSection>
        </TweaksPanel>
      )}
    </>
  );
}
